import { cloneDeep } from 'lodash';
import { useEffect, useRef } from 'react';
import { useFramework, useIdentity } from '@/framework';
import {
    REFERENCE_LINE_DIRECTION,
    REFERENCE_LINE_EVENT_NAMES,
    ResizeOperateType,
    type ReferenceLine,
} from '@/version-types';
import { getReferenceLinesState } from '../states/reference-lines-state';

// 监听画布尺寸更新, 对应调整参考线的位置
export const useReferenceLinesEvents = () => {
    const identity = useIdentity();
    const framework = useFramework();
    const localReferenceLinesRef = useRef<ReferenceLine[]>([]);
    const setReferenceLines = getReferenceLinesState(identity).setReferenceLines;
    const updateReferenceLine = getReferenceLinesState(identity).updateReferenceLine;

    const originStageRect = useRef<{
        top: number;
        bottom: number;
        height: number;
        width: number;
    }>({
        top: 0,
        bottom: 0,
        height: 0,
        width: 0,
    });

    const getStageRect = () => {
        const stageIndex = framework.stage.getActiveStageIndex();
        const { width, height } = framework.stage.getActiveStageSize();
        const top = framework.getStageTop(stageIndex);
        const bottom = top + height;
        return {
            top,
            bottom,
            height,
            width,
        };
    };

    const adjustReferenceLines = (newSize: number, oldSize: number, operateType: ResizeOperateType) => {
        const deltaSize = newSize - oldSize;
        const oldBottom = originStageRect.current.bottom;
        const oldTop = originStageRect.current.top;
        const newRight = framework.stage.getSize().width;

        // 重置所有参考线
        setReferenceLines(localReferenceLinesRef.current);

        switch (operateType) {
            case ResizeOperateType.BC:
                localReferenceLinesRef.current.forEach((line) => {
                    if (line.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                        const newPosition = line.position + deltaSize;
                        if (line.position > oldBottom) {
                            updateReferenceLine({
                                id: line.id,
                                position: newPosition,
                            });
                        }
                    }
                });
                break;
            case ResizeOperateType.TC:
                localReferenceLinesRef.current.forEach((line) => {
                    if (line.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                        const newPosition = line.position + deltaSize;
                        if (line.position > oldTop) {
                            updateReferenceLine({
                                id: line.id,
                                position: newPosition,
                            });
                        }
                    }
                });
                break;
            case ResizeOperateType.LC:
                localReferenceLinesRef.current.forEach((line) => {
                    if (line.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                        const newPosition = line.position + deltaSize;
                        // 拖动左侧，全部参考线都要跟着调整
                        updateReferenceLine({
                            id: line.id,
                            position: newPosition,
                        });
                    }
                });
                break;
            case ResizeOperateType.RC:
                localReferenceLinesRef.current.forEach((line) => {
                    if (line.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                        const newPosition = line.position + deltaSize;
                        // 拖动右侧，只有新画布 Rect 右侧的参考线需要跟着调整
                        if (line.position > newRight) {
                            updateReferenceLine({
                                id: line.id,
                                position: newPosition,
                            });
                        }
                    }
                });
                break;
        }
    };

    // 舞台尺寸调整之前
    useEffect(() => {
        const handler = framework.events.on(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_START, () => {
            const lines = getReferenceLinesState(identity).referenceLines;
            localReferenceLinesRef.current = cloneDeep(lines);

            // 保存旧的舞台尺寸信息
            originStageRect.current = getStageRect();
        });

        return () => framework.events.off(handler);
    }, []);

    // 舞台尺寸调整
    useEffect(() => {
        const handler = framework.events.on(
            REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZING,
            ({ newSize, oldSize, operateType }) => {
                adjustReferenceLines(newSize, oldSize, operateType);
            },
        );

        return () => framework.events.off(handler);
    }, []);

    // 舞台尺寸调整之后
    useEffect(() => {
        const handler = framework.events.on(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_END, () => {
            localReferenceLinesRef.current = [];
        });

        return () => framework.events.off(handler);
    }, []);
};
